import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import Button from './../../../components/Buttons/Button';
import ButtonBar from './../../../components/Fields/ButtonBar';
import Form from './../../../components/Fields/Form';
import MultiSelectField from './../../../components/Fields/MultiSelectField';
import SelectListField from './../../../components/Fields/SelectListField';
import TextboxField from './../../../components/Fields/TextboxField';
import ActionMenu from '../../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../../components/ActionMenu/ActionMenuItem';
import Item_popup_CreateItem from './../Popups/Item_popup_CreateItem';
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import SwitchField from '../../../components/Fields/SwitchField';

export interface ItemFilterFormProps {
    projectId?: number
    projectTypeId?: number
    productCategoryId?: number
    forBom: boolean
    handleSearch: (criteria: ItemSearchCriteria) => void
}

export interface ItemSearchCriteria {
    quickSearch: string
    itemTypeIds: number[]
    brandId?: number
    productCategoryId?: number
    projectTypeId?: number
    activeOnly: boolean
    mainItemsOnly: boolean
    includeUnsearchableItems: boolean
    forBom: boolean
    rememberFilter: boolean

    pageNumber: number
    pageSize: number
    sortBy: string

    fromNavBar: boolean
}

const ItemFilterForm: React.FC<ItemFilterFormProps> = ({ projectId, projectTypeId, productCategoryId, forBom, handleSearch }) => {
    let includeUnsearchableItems = false
    if (projectTypeId || productCategoryId)
        includeUnsearchableItems = true

    const [formData, setFormData] = useState<ItemSearchCriteria>({
        quickSearch: '',
        itemTypeIds: [],
        brandId: 0,
        productCategoryId: productCategoryId,
        projectTypeId: projectTypeId,
        activeOnly: true,
        mainItemsOnly: true,
        forBom: forBom,
        includeUnsearchableItems: includeUnsearchableItems,
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'CreatedOn',
        fromNavBar: false,
        rememberFilter: false,

    });

    let itemTypesUrl = `api/v1/selectlist/SearchableItemTypes?activeOnly=${formData.activeOnly}&applicationFunctionId=${ApplicationFunctions.SearchItemRevision}`;
    if (projectTypeId)
        itemTypesUrl = `api/v1/selectlist/ItemTypes?activeOnly=${formData.activeOnly}&projectTypeId=${projectTypeId}&applicationFunctionId=${ApplicationFunctions.SearchItemRevision}`;
    if (forBom == true)
        itemTypesUrl = 'api/v1/selectlist/ItemTypesForBom'

    let itemTypeId = 0
    if (formData.itemTypeIds.length > 0)
        itemTypeId = formData.itemTypeIds[0]

    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [showCreateItemPopup, setShowCreateItemPopup] = useState<boolean>(false);
    const itemTypes = useSelectListOptions(itemTypesUrl);
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees?activeOnly=true', '-- Select brand');
    const productCategories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTreeLeaves?activeOnly=true&itemTypeId=' + itemTypeId, '-- Select productcategory');

    useEffect(() => {
        const filterItem = localStorage.getItem(`itemFilterForm_forBom=${forBom}_forProject=${projectTypeId!==undefined}`);
        if (filterItem !== null) {
            let filter: ItemSearchCriteria = JSON.parse(filterItem);
            const fromNavBar = filter.fromNavBar;

            filter.fromNavBar = false;
            filter.projectTypeId = projectTypeId;
            filter.productCategoryId = productCategoryId;
            filter.forBom = forBom;
            filter.mainItemsOnly = true;
            setFormData({ ...filter });

            localStorage.setItem(`itemFilterForm_forBom=${forBom}_forProject=${projectTypeId !== undefined}`, JSON.stringify(filter));

            if (fromNavBar === true)
                handleSearch(filter);
        }
        else {
            formData.projectTypeId = projectTypeId
            formData.productCategoryId = productCategoryId
            setFormData({ ...formData })
        }
    }, [productCategoryId, projectTypeId])

    function search() {
        if (formData.rememberFilter)
            localStorage.setItem(`itemFilterForm_forBom=${forBom}_forProject=${projectTypeId !== undefined}`, JSON.stringify(formData));
        else
            localStorage.removeItem(`itemFilterForm_forBom=${forBom}_forProject=${projectTypeId !== undefined}`);

        handleSearch(formData);
        setShowFilter(false);
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'itemTypeIds', label: 'Item types', labelSize: 2 },
            { name: 'brandId', label: 'Brand', labelSize: 2 },
            { name: 'productCategoryId', label: 'Category', labelSize: 2 },
            { name: 'activeOnly', label: 'Active items only', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },

        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);
    const { isAuthorizedForItemType } = useAuth();

    const formActions = !isAuthorizedForItemType(ApplicationFunctions.CreateItem)
        ? undefined
        : <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new item" onClick={() => { setShowCreateItemPopup(true); }} appFunction={ApplicationFunctions.CreateItem} itemTypeId={0} />
          </ActionMenu>

    return (
        <>
            {showFilter && (
                <Form title='Search itemrevisons' actions={formActions} footer={(
                    <ButtonBar offset={2}>
                        <Button text='Search' {...getSubmitButtonProps()} />
                    </ButtonBar>
                )}>
                    <Container fluid>
                        <TextboxField {...getFieldProps('quickSearch')} />
                        <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} visible={productCategoryId === undefined} />
                        <SelectListField {...getFieldProps('brandId')} options={brands} />
                        <SelectListField {...getFieldProps('productCategoryId')} options={productCategories} visible={productCategoryId === undefined} />
                        {forBom == false && <SwitchField {...getFieldProps('activeOnly')} />}
                        <SwitchField {...getFieldProps('rememberFilter')} />
                    </Container>
                </Form>
            )}
            {!showFilter && (
                <ButtonBar offset={0}>
                    <Button text='Show filter' onClick={() => setShowFilter(true)} />
                </ButtonBar>
            )}
            <Item_popup_CreateItem projectId={projectId} projectTypeId={projectTypeId} show={showCreateItemPopup} onHide={() => setShowCreateItemPopup(false)} />
        </>
    );
}
export default ItemFilterForm;